import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import AdminUserApi from "../Api/AdminUserApi";
import RoleApi from "../Api/RoleApi";
import usePermissions from "../hook/usePermissions";
import Select from "react-select";
import { selectStyles } from "../helper/SelectStyles";
import { toast } from "react-toastify";

const AdminUserFormLayer = () => {
    const { hasPermission } = usePermissions();
    const navigate = useNavigate();
    const { id } = useParams();
    const isEdit = !!id;

    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        name: "",
        phoneNumber: "",
        email: "",
        pin: "",
        roleId: "",
        isActive: 1,
    });

    const [roleOptions, setRoleOptions] = useState([]);
    const [errors, setErrors] = useState({});
    const [pin, setPin] = useState(["", "", "", ""]);
    const [showPin, setShowPin] = useState(false);
    const pinRefs = useRef([]);

    useEffect(() => {
        fetchRoles();
        if (isEdit) {
            getAdminUserById(id);
        }
    }, [isEdit, id]);

    const fetchRoles = async () => {
        const response = await RoleApi.getRoles({ limit: 100 });
        if (response && response.status && response.response.data) {
            const roles = response.response.data.map((role) => ({
                value: role._id,
                label: role.name,
            }));
            setRoleOptions(roles);
        }
    };

    const getAdminUserById = async (id) => {
        setLoading(true);
        const response = await AdminUserApi.getAdminUserById(id);
        if (response && response.status && response.response.data) {
            const data = response.response.data;
            setFormData({
                name: data.name || "",
                phoneNumber: data.phoneNumber || "",
                email: data.email || "",
                pin: "", // PIN is not fetched for security
                roleId: data.roleId?._id || data.roleId || "",
                isActive: data.isActive ?? 1,
            });
            setPin(["•", "•", "•", "•"]);
        }
        setLoading(false);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === "phoneNumber") {
            const cleaned = value.replace(/\D/g, "").slice(0, 10);
            setFormData((prev) => ({ ...prev, [name]: cleaned }));
        } else {
            setFormData((prev) => ({ ...prev, [name]: value }));
        }
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
    };

    const handlePinChange = (index, value) => {
        // Remove bullets to get the actual input
        let char = value.replace(/•/g, "");
        if (char.length > 1) char = char.slice(-1);

        if (char && isNaN(char)) return;

        const newPin = [...pin];
        newPin[index] = char;
        setPin(newPin);

        // Update formData.pin as a joined string of digits only
        const joinedPin = newPin.filter(c => c !== "•" && c !== "").join("");
        setFormData(prev => ({ ...prev, pin: joinedPin }));

        if (errors.pin) setErrors((prev) => ({ ...prev, pin: "" }));

        if (char && index < 3) {
            pinRefs.current[index + 1].focus();
        }
    };

    const handlePinKeyDown = (index, e) => {
        if (e.key === "Backspace" && !pin[index] && index > 0) {
            pinRefs.current[index - 1].focus();
        }
        // Block signs like + and - as well as 'e' for scientific notation
        if (["e", "E", "+", "-"].includes(e.key)) {
            e.preventDefault();
        }
    };

    const handleKeyDown = (e) => {
        // Block signs like + and - as well as 'e' for scientific notation
        if (["e", "E", "+", "-"].includes(e.key)) {
            e.preventDefault();
        }
    };

    const handleSelectChange = (selectedOption) => {
        setFormData((prev) => ({ ...prev, roleId: selectedOption?.value || "" }));
        if (errors.roleId) setErrors(prev => ({ ...prev, roleId: "" }));
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.name.trim()) newErrors.name = "Name is required";
        if (!formData.phoneNumber.trim()) newErrors.phoneNumber = "Phone number is required";
        if (formData.phoneNumber.length < 10) newErrors.phoneNumber = "Invalid phone number";

        if (!isEdit) {
            if (!formData.email.trim()) newErrors.email = "Email is required";
            else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = "Invalid email format";
            if (!formData.pin.trim() || formData.pin.length !== 4) newErrors.pin = "Complete 4-digit PIN is required";
        } else {
            if (formData.pin.length > 0 && formData.pin.length < 4) {
                newErrors.pin = "Enter a complete 4-digit PIN to update it";
            }
        }

        if (!formData.roleId) newErrors.roleId = "Role is required";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setLoading(true);
        try {
            let response;
            if (isEdit) {
                const payload = {
                    name: formData.name,
                    phoneNumber: formData.phoneNumber,
                    roleId: formData.roleId,
                    isActive: formData.isActive
                };
                if (formData.pin.length === 4) {
                    payload.pin = formData.pin;
                }
                response = await AdminUserApi.updateAdminUser(id, payload);
            } else {
                const payload = { ...formData };
                response = await AdminUserApi.createAdminUser(payload);
            }

            if (response && response.status) {
                navigate("/admin-registration");
            }
        } catch (error) {
            console.error("API Error:", error);
            toast.error("An error occurred. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    if (loading && isEdit && !formData.name) {
        return <div className="p-24 text-center">Loading user details...</div>;
    }

    return (
        <div className="card h-100 p-0 radius-12">
            <div className="card-header border-bottom bg-base py-16 px-24">
                <h6 className="text-primary-600 pb-2 mb-0" style={{ color: '#364E3D' }}>
                    {isEdit ? "Edit Admin User" : "Create New Admin User"}
                </h6>
            </div>
            <div className="card-body p-24">
                <form onSubmit={handleSubmit} noValidate>
                    <div className="row gy-4">
                        <div className="col-md-6">
                            <label className="form-label fw-semibold text-secondary-light">Full Name <span className="text-danger">*</span></label>
                            <input
                                type="text"
                                className={`form-control radius-8 ${errors.name ? 'is-invalid' : ''}`}
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                placeholder="Enter Full Name"
                            />
                            {errors.name && <small className="text-danger">{errors.name}</small>}
                        </div>

                        <div className="col-md-6">
                            <label className="form-label fw-semibold text-secondary-light">Email Address {!isEdit && <span className="text-danger">*</span>}</label>
                            <input
                                type="email"
                                className={`form-control radius-8 ${errors.email ? 'is-invalid' : ''}`}
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="Enter Email Address"
                                disabled={isEdit}
                            />
                            {errors.email && <small className="text-danger">{errors.email}</small>}
                        </div>

                        <div className="col-md-6">
                            <label className="form-label fw-semibold text-secondary-light">Phone Number <span className="text-danger">*</span></label>
                            <input
                                type="text"
                                className={`form-control radius-8 ${errors.phoneNumber ? 'is-invalid' : ''}`}
                                name="phoneNumber"
                                value={formData.phoneNumber}
                                onChange={handleChange}
                                onKeyDown={handleKeyDown}
                                placeholder="Enter 10-digit Phone Number"
                                maxLength={10}
                            />
                            {errors.phoneNumber && <small className="text-danger">{errors.phoneNumber}</small>}
                        </div>

                        <div className="col-md-6">
                            <label className="form-label fw-semibold text-secondary-light">Select Role <span className="text-danger">*</span></label>
                            <Select
                                options={roleOptions}
                                value={roleOptions.find((opt) => opt.value === formData.roleId)}
                                onChange={handleSelectChange}
                                styles={selectStyles(!!errors.roleId)}
                                placeholder="-- Select Role --"
                            />
                            {errors.roleId && <div className="text-danger mt-1" style={{ fontSize: '12px' }}>{errors.roleId}</div>}
                        </div>

                        <div className="col-md-6">
                            <label className="form-label fw-semibold text-secondary-light">Login PIN <span className="text-danger">*</span></label>
                            <div className="d-flex align-items-center gap-3">
                                <div className="d-flex gap-2">
                                    {pin.map((digit, index) => (
                                        <input
                                            key={index}
                                            ref={(el) => (pinRefs.current[index] = el)}
                                            type={showPin ? "text" : "password"}
                                            inputMode="numeric"
                                            className={`form-control text-center fw-bold radius-8 ${errors.pin ? 'is-invalid border-danger' : ''}`}
                                            style={{ width: "50px", height: "50px", fontSize: "1.2rem" }}
                                            value={digit}
                                            onChange={(e) => handlePinChange(index, e.target.value)}
                                            onKeyDown={(e) => handlePinKeyDown(index, e)}
                                            onFocus={(e) => e.target.select()}
                                            disabled={loading}
                                        />
                                    ))}
                                </div>
                                {!isEdit && (
                                    <button
                                        type="button"
                                        className="btn btn-sm border-0 p-0 text-secondary shadow-none ms-2"
                                        onClick={() => setShowPin(!showPin)}
                                        tabIndex="-1"
                                    >
                                        <Icon
                                            icon={showPin ? "lucide:eye-off" : "lucide:eye"}
                                            className="text-2xl"
                                        />
                                    </button>
                                )}
                            </div>
                            {errors.pin && <small className="text-danger d-block mt-1">{errors.pin}</small>}
                        </div>

                        <div className="col-md-6">
                            <label className="form-label fw-semibold text-secondary-light">Status</label>
                            <Select
                                options={[
                                    { value: 1, label: "Active" },
                                    { value: 0, label: "Inactive" },
                                ]}
                                value={{ value: formData.isActive, label: formData.isActive === 1 ? "Active" : "Inactive" }}
                                onChange={(opt) => setFormData({ ...formData, isActive: opt.value })}
                                styles={selectStyles()}
                                placeholder="Select Status"
                            />
                        </div>
                    </div>

                    <div className="d-flex justify-content-end gap-3 mt-40">
                        <Link
                            to="/admin-registration"
                            className="btn btn-outline-danger-600 px-32 radius-8 d-flex align-items-center justify-content-center"
                            style={{ width: "130px", height: "48px" }}
                        >
                            Cancel
                        </Link>
                        {hasPermission("Admin Users", isEdit ? "edit" : "add") && (
                            <button
                                type="submit"
                                className="btn btn-primary radius-8 px-20 py-11 d-flex align-items-center justify-content-center"
                                disabled={loading}
                                style={{ backgroundColor: "#364e3d", borderColor: "#364e3d", width: "130px", height: "48px" }}
                            >
                                {loading ? "Saving..." : isEdit ? "Update User" : "Create User"}
                            </button>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AdminUserFormLayer;

